import React from "react";
import { useState } from "react";
import { Form, Button, Modal, ModalProps } from "antd";
import { validateFields } from "../../../../../config/Global";
import { assignErrorToInput } from "../../../../../store/api";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import { createRecord } from "../../utils/slice";
import FormComponent from "./FormComponent";
import { useAppDispatch } from "../../../../../utils/hooks/hooks";

interface AddDrawerProps extends ModalProps {
  titleName: string;
  close: () => void;
}

/**
 * Renders the AddComponent component for adding a record in the database for 
 * Veteran Status Management module.
 *
 * @param {AddDrawerProps} props - The component props.
 * @param {string} props.titleName - The title name.
 * @param {() => void} props.close - The function to close the component.
 * @param {...rest} props.rest - The rest of the component props.
 * @return {ReactElement} The rendered AddComponent component.
 */
const AddComponent: React.FC<AddDrawerProps> = ({
  titleName,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true)
  };

  // validate form on values change event and enable/disable the submit button
  const validateForm = () => {
    validateFields(form, setDisabled);
  };

  // handle form submit
  const handleFormSubmit = (data: any) => {
    setSaving(true);

    dispatch(createRecord(data))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      title={`Add ${titleName}`}
      centered
      width={"35%"}
      onCancel={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[
        <Button
          key="2"
          htmlType="button"
          className="cancelBtn"
          onClick={drawerClose}
        >
          Cancel
        </Button>,
        <Button
          key="1"
          disabled={disabled}
          form="addForm"
          loading={saving}
          htmlType="submit"
          type="primary"
        >
          Save
        </Button>,
      ]}
      {...rest}
    >
      <FormComponent
        form={form}
        id="addForm"
        handleSubmit={handleFormSubmit}
        onValuesChange={validateForm}
      />
    </Modal>
  );
};

export default AddComponent;
